import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, X, Loader2 } from 'lucide-react';

const GIORNI = [
  { key: 'lun', label: 'Lunedì' }, { key: 'mar', label: 'Martedì' }, { key: 'mer', label: 'Mercoledì' },
  { key: 'gio', label: 'Giovedì' }, { key: 'ven', label: 'Venerdì' }, { key: 'sab', label: 'Sabato' }, { key: 'dom', label: 'Domenica' },
];

export const OrariDialog = ({ open, onOpenChange }) => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [targetId, setTargetId] = useState('me');

  const settingsUrl = targetId === 'me' ? '/booking/settings' : `/booking/settings/${targetId}`;

  useEffect(() => {
    if (open && isAdmin) api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    if (!open) setTargetId('me');
  }, [open, isAdmin]);

  useEffect(() => {
    if (open) {
      setSettings(null);
      api.get(settingsUrl).then(r => setSettings(r.data)).catch(() => toast.error('Errore caricamento orari'));
    }
  }, [open, settingsUrl]);

  const setRange = (giorno, idx, field, value) => {
    setSettings(s => {
      const orari = { ...s.orari };
      const ranges = [...(orari[giorno] || [])];
      ranges[idx] = { ...ranges[idx], [field]: value };
      orari[giorno] = ranges;
      return { ...s, orari };
    });
  };

  const addRange = (giorno) => setSettings(s => ({ ...s, orari: { ...s.orari, [giorno]: [...(s.orari[giorno] || []), { start: '09:00', end: '13:00' }] } }));
  const removeRange = (giorno, idx) => setSettings(s => ({ ...s, orari: { ...s.orari, [giorno]: (s.orari[giorno] || []).filter((_, i) => i !== idx) } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(settingsUrl, settings);
      toast.success('Orari salvati');
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Orari di disponibilità</DialogTitle></DialogHeader>

        {isAdmin && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
            <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wider mb-1.5">Gestisci orari di (admin)</p>
            <select value={targetId} onChange={e => setTargetId(e.target.value)}
              className="w-full px-2 py-1.5 border border-blue-200 rounded-lg text-sm bg-white" data-testid="orari-target-select">
              <option value="me">I miei orari</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.nome} {u.cognome} — {u.ruolo === 'collaboratore' ? 'Collaboratore' : u.ruolo === 'super_admin' ? 'Admin' : 'Dipendente'}</option>
              ))}
            </select>
          </div>
        )}

        {!settings ? <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={20} /></div> : (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-slate-800">Riceve appuntamenti</p>
                <p className="text-[11px] text-slate-500">Se disattivato NON riceve appuntamenti: escluso da pagina pubblica, Agenda Team e menu "Assegna a"</p>
              </div>
              <input type="checkbox" checked={settings.riceve_appuntamenti !== false} onChange={e => setSettings(s => ({ ...s, riceve_appuntamenti: e.target.checked }))}
                className="h-5 w-5 rounded accent-amber-600" data-testid="riceve-appuntamenti-toggle" />
            </div>
            <div className={`flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2.5 ${settings.riceve_appuntamenti === false ? 'opacity-40 pointer-events-none' : ''}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">Prenotabile online</p>
                <p className="text-[11px] text-slate-400">Se disattivato non compare nella pagina pubblica di prenotazione</p>
              </div>
              <input type="checkbox" checked={settings.attivo} onChange={e => setSettings(s => ({ ...s, attivo: e.target.checked }))}
                className="h-5 w-5 rounded accent-slate-800" data-testid="booking-attivo-toggle" />
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-600">Durata slot:</p>
              <select value={settings.slot_minuti} onChange={e => setSettings(s => ({ ...s, slot_minuti: Number(e.target.value) }))}
                className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="slot-minuti-select">
                {[15, 30, 45, 60].map(m => <option key={m} value={m}>{m} minuti</option>)}
              </select>
            </div>
            <div className="space-y-3">
              {GIORNI.map(g => (
                <div key={g.key} className="border border-slate-100 rounded-lg p-3" data-testid={`orari-${g.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">{g.label}</p>
                    <button onClick={() => addRange(g.key)} className="text-[10px] text-blue-600 flex items-center gap-0.5 hover:underline"><Plus size={11} /> fascia</button>
                  </div>
                  {(settings.orari[g.key] || []).length === 0 ? (
                    <p className="text-[11px] text-slate-300">Non disponibile</p>
                  ) : (settings.orari[g.key] || []).map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-1.5">
                      <Input type="time" value={r.start} onChange={e => setRange(g.key, idx, 'start', e.target.value)} className="h-8 text-xs w-28" />
                      <span className="text-xs text-slate-400">→</span>
                      <Input type="time" value={r.end} onChange={e => setRange(g.key, idx, 'end', e.target.value)} className="h-8 text-xs w-28" />
                      <button onClick={() => removeRange(g.key, idx)} className="text-slate-300 hover:text-red-500"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-slate-800 hover:bg-slate-700 text-white" data-testid="save-orari-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Salva orari
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
